import React, { useEffect, useState, useCallback, useMemo } from 'react'
import { useSelector } from 'react-redux'
import {
  getServiceListLoadingSelector,
  getServiceListSelector,
} from '_modules/service-orders/selectors'
import { useFormContext, useFieldArray, get, useWatch } from 'react-hook-form'
import { useDialog } from '_/hooks/use-dialog'
import { ExpandCollapseButton } from '../Buttons'
import { strMaxLenMessage } from '_/utils/form-validations'
import { NumericInput } from '_/views/budget-view-v2/components/Inputs/NumericInput'

import { Collapse, InputLabel, TextField } from '@material-ui/core'
import Autocomplete from '_components/inputs/Autocomplete'
import IconButton from '_/components/svg/icon-button'
import { Button } from '@refera/ui-web'
import { Trash } from '@refera/ui-icons'
import { useToast } from '_/hooks/use-toast'
import Theme from '@refera/ui-core'
import { MaterialsTable } from '../materials-table'
import { useInspectionLoading } from '../../hooks/use-inspection-loading'
import { useLaborValueUpdate } from '../../hooks/use-labor-value-update'
import { useExecutionDueUpdate } from '../../hooks/use-execution-due-update'
import { useServiceLaborUpdate } from '../../hooks/use-service-labor-update'
import { MultilineInput } from '_/components/inputs'

import useStyles, { useResolutionsStyles } from '../Accordions/styles'

const ResolutionMultilineInput = React.memo(function ResolutionMultilineInput({
  name,
  label,
  defaultValue = '',
  disabled,
  labelClasses,
}) {
  const { control } = useFormContext()

  const watchedValue = useWatch({
    control,
    name,
    defaultValue,
  })
  const inputRef = React.useRef(null)

  useEffect(() => {
    const nextValue = watchedValue ?? ''
    // Intencionalmente não-controlado enquanto focado para evitar re-render a cada tecla.
    if (
      inputRef.current &&
      document.activeElement !== inputRef.current &&
      inputRef.current.value !== nextValue
    ) {
      inputRef.current.value = nextValue
    }
  }, [watchedValue])

  return (
    <MultilineInput
      defaultValue={defaultValue}
      name={name}
      label={label}
      labelClasses={labelClasses}
      required
      rules={{ maxLength: { value: 5000, message: strMaxLenMessage(5000) } }}
      minRows={5}
      maxRows={100}
      disabled={disabled}
      ref={inputRef}
    />
  )
})

const getServiceOptions = services => {
  if (Array.isArray(services?.results)) return services.results
  if (Array.isArray(services)) return services
  return []
}

const hasServiceOptionsShape = services =>
  Array.isArray(services?.results) || Array.isArray(services)

const ResolutionRow = React.memo(function ResolutionRow({
  field,
  index,
  name,
  compartmentIndex,
  open,
  openDetails,
  services,
  loadingServices,
  isEditing,
  isOriginal,
  budgetType,
  toggleResolution,
  toggleMaterial,
  handleRemove,
  handleServiceDataUpdate,
  getAndUpdateLaborValue,
  updateExecutionDue,
}) {
  const styles = useStyles()
  const resolutionsStyles = useResolutionsStyles()
  const {
    control,
    formState: { errors },
  } = useFormContext()
  const isReadOnly = !isEditing || isOriginal

  const servicePath = `${name}.${index}.service`
  const watchedService = useWatch({
    control,
    name: servicePath,
    defaultValue: field.service,
  })

  const hasError = get(errors, servicePath)
  const serviceId = watchedService?.id ?? watchedService
  const serviceFromList = services.find(service => String(service?.id) === String(serviceId))
  const resolvedService =
    watchedService && typeof watchedService === 'object'
      ? { ...serviceFromList, ...watchedService }
      : serviceFromList
  const laborTypeLabel = resolvedService?.laborType?.name || resolvedService?.laborType?.slug || '-'

  return (
    <div className={resolutionsStyles.main}>
      <div className={styles.compartmentHeader}>
        <ExpandCollapseButton open={open} onToggle={() => toggleResolution(field.fieldId)} />

        <InputLabel
          variant="standard"
          error={hasError}
          className={styles.label}
          disabled={isReadOnly}
        >
          Serviço<span className={styles.required}>*</span>
        </InputLabel>
      </div>

      <div>
        <div className={`${styles.row} ${styles.rowSingle}`}>
          <Autocomplete
            name={servicePath}
            required
            options={services}
            multiple={false}
            loading={loadingServices}
            getOptionLabel={option => `${option.externalId} - ${option.name || ''}`}
            getOptionSelected={(option, value) => option.id === value?.id}
            autocompleteClasses={styles.compactAutocomplete}
            defaultValue={field.service}
            disabled={isReadOnly || loadingServices}
            enableContainsSearch
            customOnChange={(event, value) => {
              if (!value) return
              handleServiceDataUpdate(`${name}.${index}`, value, compartmentIndex, index)
            }}
          />

          <div className={styles.actions}>
            <IconButton variant="ghost" onClick={() => handleRemove(index)} disabled={isReadOnly}>
              <Trash
                color={isReadOnly ? Theme.Colors.Grayscale.ThirtyTwo : Theme.Colors.Red.Base}
                fontSize={20}
              />
            </IconButton>
          </div>
        </div>

        <Collapse in={open}>
          <div className={styles.row} style={{ paddingTop: '4px' }}>
            <ResolutionMultilineInput
              defaultValue={field?.inspectionDescription}
              name={`${name}.${index}.inspectionDescription`}
              labelClasses={styles.label}
              label="Descrição de vistoria"
              disabled={isReadOnly}
            />
            <ResolutionMultilineInput
              defaultValue={field?.serviceDescription}
              name={`${name}.${index}.serviceDescription`}
              labelClasses={styles.label}
              label="Descrição de serviço"
              disabled={isReadOnly}
            />
          </div>

          <div className={`${styles.row} ${styles.materialsHeader}`}>
            <ExpandCollapseButton
              open={openDetails}
              onToggle={() => toggleMaterial(field.fieldId)}
            />

            <InputLabel variant="standard" className={styles.label}>
              Descrição detalhada
            </InputLabel>
          </div>
          <div className={styles.laborSummaryRow}>
            <div className={styles.laborTypeField}>
              <InputLabel
                variant="standard"
                className={styles.label}
                disabled
                style={{ marginBottom: '6px' }}
              >
                Tipo de MO
              </InputLabel>
              <TextField
                variant="outlined"
                value={laborTypeLabel}
                fullWidth
                disabled
                className={styles.readOnlyField}
              />
            </div>
            <NumericInput
              name={`${name}.${index}.laborTime`}
              required
              label="Tempo MO (h)"
              placeholder="0,00"
              prefix=""
              defaultValue={field?.laborTime || 0}
              disabled={isReadOnly}
              onBlur={() => {
                getAndUpdateLaborValue({
                  compartmentIndex,
                  resolutionIndex: index,
                })
                updateExecutionDue()
              }}
            />
            <NumericInput
              name={`${name}.${index}.laborValue`}
              label="Mão de obra (ideal)"
              placeholder="R$ 0,00"
              prefix="R$ "
              defaultValue={field?.laborValue || 0}
              disabled
            />
            {budgetType === 'insurance' && (
              <>
                <NumericInput
                  name={`${name}.${index}.laborValueMax`}
                  label="Mão de obra (max)"
                  placeholder="R$ 0,00"
                  prefix="R$ "
                  defaultValue={field?.laborValueMax || 0}
                  disabled
                />
                <NumericInput
                  name={`${name}.${index}.agencyValue`}
                  label="Valor da imobiliária"
                  placeholder="R$ 0,00"
                  prefix="R$ "
                  defaultValue={field?.agencyValue ?? null}
                  disabled={isReadOnly}
                  required
                />
              </>
            )}
          </div>
          <Collapse in={openDetails} mountOnEnter unmountOnExit>
            <div className={styles.detailsContainer}>
              <MaterialsTable
                name={`${name}.${index}.budgetResolutionMaterials`}
                compartmentIndex={compartmentIndex}
                isReadOnly={isReadOnly}
              />
            </div>
          </Collapse>
        </Collapse>
      </div>
    </div>
  )
},
areResolutionRowPropsEqual)

const RESOLUTION_FIELD_PROPS_TO_COMPARE = [
  'fieldId',
  'service',
  'inspectionDescription',
  'serviceDescription',
  'laborTime',
  'laborValue',
  'laborValueMax',
  'agencyValue',
  'budgetResolutionMaterials',
]

function areResolutionFieldsEqual(prevField, nextField) {
  if (prevField === nextField) return true

  return RESOLUTION_FIELD_PROPS_TO_COMPARE.every(key => prevField?.[key] === nextField?.[key])
}

function areResolutionRowPropsEqual(prevProps, nextProps) {
  return (
    areResolutionFieldsEqual(prevProps.field, nextProps.field) &&
    prevProps.index === nextProps.index &&
    prevProps.name === nextProps.name &&
    prevProps.compartmentIndex === nextProps.compartmentIndex &&
    prevProps.open === nextProps.open &&
    prevProps.openDetails === nextProps.openDetails &&
    prevProps.services === nextProps.services &&
    prevProps.loadingServices === nextProps.loadingServices &&
    prevProps.isEditing === nextProps.isEditing &&
    prevProps.isOriginal === nextProps.isOriginal &&
    prevProps.budgetType === nextProps.budgetType &&
    prevProps.toggleResolution === nextProps.toggleResolution &&
    prevProps.toggleMaterial === nextProps.toggleMaterial &&
    prevProps.handleRemove === nextProps.handleRemove &&
    prevProps.handleServiceDataUpdate === nextProps.handleServiceDataUpdate &&
    prevProps.getAndUpdateLaborValue === nextProps.getAndUpdateLaborValue &&
    prevProps.updateExecutionDue === nextProps.updateExecutionDue
  )
}

export function ResolutionsTable({ name, compartmentIndex }) {
  const { showToast } = useToast()
  const services = useSelector(getServiceListSelector)
  const servicesLoading = useSelector(getServiceListLoadingSelector)
  const serviceOptions = useMemo(() => getServiceOptions(services), [services])
  const { budget, isEditing, budgetType } = useInspectionLoading()
  const isReadOnly = !isEditing || budget?.isOriginal

  const { closeDialog, showDialog } = useDialog()
  const [openResolutions, setOpenResolutions] = useState({})
  const [openMaterials, setOpenMaterials] = useState({})
  const loadingServices = servicesLoading || !hasServiceOptionsShape(services)

  const { control, setValue, getValues } = useFormContext()

  const { fields, append, remove } = useFieldArray({
    control,
    name,
    keyName: 'fieldId',
  })

  useEffect(() => {
    setOpenResolutions(prev => {
      let hasChanges = false
      const next = { ...prev }
      fields.forEach(field => {
        if (next[field.fieldId] === undefined) {
          next[field.fieldId] = true
          hasChanges = true
        }
      })
      return hasChanges ? next : prev
    })

    setOpenMaterials(prev => {
      let hasChanges = false
      const next = { ...prev }
      fields.forEach(field => {
        if (next[field.fieldId] === undefined) {
          next[field.fieldId] = true
          hasChanges = true
        }
      })
      return hasChanges ? next : prev
    })
  }, [fields])

  const { getAndUpdateLaborValue } = useLaborValueUpdate({ getValues, setValue, budgetType })
  const { updateExecutionDue } = useExecutionDueUpdate({ getValues, setValue })
  const { handleServiceDataUpdate } = useServiceLaborUpdate({
    getValues,
    setValue,
    showToast,
    getAndUpdateLaborValue,
    updateExecutionDue,
  })

  const handleRemove = useCallback(
    index => {
      const currentResolutions = getValues(name) || []
      if (currentResolutions.length === 1) {
        showToast({
          type: 'warning',
          message: 'É necessário ter pelo menos um serviço',
        })
        return
      }
      showDialog({
        type: 'warning',
        subject: 'Você tem certeza que deseja excluir esse registro?',
        labelApprove: 'Confirmar',
        labelCancel: 'Cancelar',
        onCancel: closeDialog,
        onApprove: () => {
          remove(index)
          Promise.resolve().then(() => {
            getAndUpdateLaborValue({ resolutionIndex: index })
            updateExecutionDue()
          })
          closeDialog()
        },
      })
    },
    [
      showDialog,
      closeDialog,
      remove,
      getValues,
      name,
      showToast,
      getAndUpdateLaborValue,
      updateExecutionDue,
    ]
  )

  const toggleResolution = useCallback(resolutionId => {
    setOpenResolutions(prev => ({
      ...prev,
      [resolutionId]: !(prev[resolutionId] ?? true),
    }))
  }, [])

  const toggleMaterial = useCallback(materialId => {
    setOpenMaterials(prev => ({
      ...prev,
      [materialId]: !(prev[materialId] ?? true),
    }))
  }, [])

  useEffect(() => {
    if (fields.length === 0) {
      append({ service: null })
    }
  }, [fields.length])

  return (
    <div style={{ paddingLeft: '40px' }}>
      {fields?.map((field, index) => {
        const open = openResolutions[field.fieldId] ?? true
        const openDetails = openMaterials[field.fieldId] ?? true
        return (
          <ResolutionRow
            key={field.fieldId}
            field={field}
            index={index}
            name={name}
            compartmentIndex={compartmentIndex}
            open={open}
            openDetails={openDetails}
            services={serviceOptions}
            loadingServices={loadingServices}
            isEditing={isEditing}
            isOriginal={budget?.isOriginal}
            budgetType={budgetType}
            toggleResolution={toggleResolution}
            toggleMaterial={toggleMaterial}
            handleRemove={handleRemove}
            handleServiceDataUpdate={handleServiceDataUpdate}
            getAndUpdateLaborValue={getAndUpdateLaborValue}
            updateExecutionDue={updateExecutionDue}
          />
        )
      })}

      <div>
        <Button
          variant="secondary"
          style={{ width: '160px', margin: '8px 0 16px 0' }}
          onClick={() => append({ service: null })}
          disabled={isReadOnly}
        >
          + Adicionar serviço
        </Button>
      </div>
    </div>
  )
}
